import React, { useState, useEffect } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { User } from '../../types/cabinet.types';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface EditContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onSave: (data: Partial<User>) => Promise<boolean>;
}

export const EditContactModal: React.FC<EditContactModalProps> = ({ isOpen, onClose, user, onSave }) => {
  const { t } = useI18n();
  const [phone, setPhone] = useState(user?.phone || '');
  const [email, setEmail] = useState(user?.email?.endsWith('@tabletka24.tmp') ? '' : (user?.email || ''));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setPhone(user?.phone || '');
      setEmail(user?.email?.endsWith('@tabletka24.tmp') ? '' : (user?.email || ''));
      setError('');
    }
  }, [isOpen, user]);

  const validate = () => {
    const numericPhone = phone.replace(/\D/g, '');
    if (numericPhone.length > 0 && numericPhone.length < 9) {
      setError(t('err_phone_min9') || 'Podaj prawidłowy numer telefonu (min. 9 cyfr)');
      return false;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('err_email_invalid') || 'Podaj prawidłowy adres e-mail');
      return false;
    }
    setError('');
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    
    setIsSaving(true);
    const success = await onSave({ phone, email });
    setIsSaving(false);
    if (success) {
      onClose();
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={t('dane_contact')} 
      subtitle={t('edit_your')}
    >
      <div className="cab-field" style={{ marginBottom: '16px' }}>
        <label>{t('phone_short')}</label>
        <input 
          type="tel" 
          className={`cab-input ${error ? 'error' : ''}`} 
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            if (error) setError('');
          }}
          placeholder="+48 000 000 000"
          disabled={isSaving}
        />
      </div>

      <div className="cab-field" style={{ marginBottom: '24px' }}>
        <label>{t('email')}</label>
        <input 
          type="email" 
          className={`cab-input ${error && error.includes('e-mail') ? 'error' : ''}`} 
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError('');
          }}
          placeholder="email@example.com"
          disabled={isSaving}
        />
        {error && <span className="cab-field-error">{error}</span>}
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <Button variant="ghost" onClick={onClose} disabled={isSaving} style={{ flex: 1 }}>
          {t('cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave} isLoading={isSaving} style={{ flex: 1 }}>
          {t('save')}
        </Button>
      </div>
    </Modal>
  );
};
